import { humanPopulation } from "src/components/ui";

/**
 * Turns audit rows into something a person can read.
 *
 * The log stores what the write path did — one row per entity touched, listing the
 * columns it set. That is the right thing to store and the wrong thing to show:
 * creating one person writes an `identity.create` and an `engagement.create` in the
 * same transaction, so the reader sees two rows of snake_case for a single action
 * they think of as "I added someone".
 *
 * So this collapses rows back into the action that caused them, and names things the
 * way the forms do. The exact record stays reachable behind the row's expander —
 * being able to answer "which column, what value, when" is the point of an audit log
 * and must not be summarised away.
 */

export type AuditRow = {
  actorIp?: string | null;
  id: string;
  occurredAt: string;
  actorEmail: string;
  action: string;
  entityType: string;
  population?: string | null;
  changedFields: readonly string[];
  before?: string | null;
  after?: string | null;
  requestId?: string | null;
};

export type AuditEvent = {
  key: string;
  occurredAt: string;
  actorEmail: string;
  population: string | null;
  rows: AuditRow[];
};

/**
 * Column name to the label the forms use, so the same field is not "legal_last_name"
 * here and "Legal last name" three inches up the page.
 *
 * The `_id` columns are the ones worth special-casing: "Need entity" is the thing the
 * user picked, "need_entity_id" is how we store the pointer to it.
 */
export const FIELD_LABELS: Record<string, string> = {
  legal_first_name: "Legal first name",
  legal_middle_name: "Legal middle name",
  legal_last_name: "Legal last name",
  legal_full_name_local: "Local legal name",
  preferred_first_name: "Preferred first name",
  preferred_middle_name: "Preferred middle name",
  preferred_last_name: "Preferred last name",
  gender: "Gender",
  // The column keeps its name; only the console label moved (INFRA-696). "Work
  // location" is HR vocabulary for a site, and this holds a country.
  work_location: "Work country",
  // The pre-rename column, under the label the field carries now. It is the same
  // fact, so history reads under one name rather than teaching a reader a term the
  // system no longer uses.
  //
  // The entry cannot be rewritten — audit_log refuses UPDATE by trigger, so every
  // snapshot written while both columns were being written holds this key forever — and
  // *deleting* this line would achieve the opposite of removing the old name:
  // humanField de-snake-cases an unmapped column, so it would render the old label from
  // the key itself. Mapping it is the only way to stop showing it.
  country_of_residence: "Work country",
  work_email: "Work email",
  personal_email: "Personal email",
  population: "Population",
  need_entity_id: "Need entity",
  need_npd_subcategory_id: "Subcategory",
  external_partner_organization_id: "Partner organization",
  title: "Title",
  dri_id: "DRI",
  department_id: "Department",
  contract_link: "Contract link",
  start_date: "Start date",
  end_date: "Contracted end",
  terminated_on: "Ended on",
  activated_at: "System access granted at",
  deactivated_at: "System access removed at",
  has_system_access: "System access",
  // Only ever changed by archive and unarchive, whose action titles already say
  // which way it went — so the label is the plain column name rather than
  // "Archived", which would read as a second claim beside "Organization retired".
  archived_at: "Archived on",
  relationship_owner: "Relationship owner",
  // No longer written: where a term sits in time is derived from the dates. Kept
  // because audit_log refuses UPDATE, so every entry from before the change still
  // carries it and would otherwise render as a raw column name.
  status: "Status",
  beam_number: "Beam number",
  beam_type: "Beam type",
  email: "Email",
  roles: "Roles",
  // No longer written, and only ever in an admin_user.grant_role snapshot, where it
  // said a grant came from the local-admins file. The action's title says that now,
  // and dropping the key made the seeder's snapshots the same shape the write path
  // records — which is what let a test hold the two to each other. Kept for the reason
  // `code`, `status` and `aliases` below are: audit_log refuses UPDATE, so every entry
  // written before that change carries this key permanently, and this map is where a
  // reader learns which keys a snapshot can contain. Local databases only.
  source: "Source",
  // No longer written: INFRA-626 stopped using external_partner_organization.code and
  // INFRA-630 dropped the column, whose only remaining job was a dedup the unique name
  // index now does. audit_log refuses
  // UPDATE by trigger, so every organization snapshot written before the drop holds
  // this key permanently and the history still has to name it.
  //
  // Unlike country_of_residence, this entry is not what makes it render acceptably:
  // the fallback de-snake-cases an unmapped column, and "code" is a single lowercase
  // word, so it comes out "Code" either way. It is listed for the reason `status`
  // above is — the map is where a reader learns which keys a snapshot can contain,
  // and a key that only appears in history is exactly the one worth writing down.
  code: "Code",
  name: "Name",
  // No longer written either: INFRA-671 stopped using
  // external_partner_organization.aliases and INFRA-672 dropped the column, which the
  // importer never used — partner organizations are matched on their name alone. Kept
  // for the same reason `code` and `status` are: every organization snapshot written
  // before that carries this key permanently, and the map is where a reader learns
  // which keys a snapshot can contain. The rendered string happens to come out
  // "Aliases" from the fallback too, so like `code` this entry is documentation rather
  // than what makes it legible — which is why audit.test.ts asserts the map and not the
  // output.
  aliases: "Aliases",
  // The former-name columns, under the labels the create form's editor uses. There is
  // no `full_name` entry, and its absence is the point: a former name's audit entry
  // deliberately carries no name value, so the key never appears in one. Mapping it
  // would suggest it could.
  name_type: "Kind",
  valid_from: "Valid from",
  valid_to: "Valid to",
  provenance: "Provenance",
  seq: "Display order"
};

/** humanField falls back to de-snake-casing, so a new column reads acceptably before
 * anyone remembers to add it above. */
export function humanField(column: string): string {
  const known = FIELD_LABELS[column];
  if (known) {
    return known;
  }
  const words = column.replace(/_id$/, "").replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The lookups a stored id can be resolved against. */
type ReferenceKind =
  | "needEntities"
  | "needNpdSubcategories"
  | "externalPartnerOrganizations"
  | "partnerDris"
  | "departments";

/**
 * The snapshot columns whose stored value is an id rather than something a person
 * can read, and the lookup each one resolves against.
 *
 * Keyed by column name, deliberately. The obvious alternative — rewrite whatever
 * looks like a UUID — would rename a value in some future column that happens to
 * hold one, and a confidently wrong name is worse than a raw id. The same rule read
 * the other way: a snapshot carries keys for columns that no longer exist (`status`,
 * `code`, `country_of_residence`), so a column being absent from this map is the
 * normal case and has to leave the value exactly as recorded.
 *
 * These three are the whole set. Everything else a snapshot holds already reads:
 * `population` stores `PARTNER`, the dates store `YYYY-MM-DD`, `beam_type` stores
 * its enum, and the identity, organization, admin-user and former-name snapshots
 * carry no ids at all.
 */
const REFERENCE_FIELDS: Record<string, ReferenceKind> = {
  need_entity_id: "needEntities",
  need_npd_subcategory_id: "needNpdSubcategories",
  external_partner_organization_id: "externalPartnerOrganizations",
  department_id: "departments",
  dri_id: "partnerDris"
};

/**
 * How an id that will not resolve is described, per lookup.
 *
 * The noun is in the value rather than left to the field label beside it, because
 * the value is what gets read on its own — copied into a question to whoever can
 * look it up, or pasted somewhere the label did not come along.
 */
const REFERENCE_NOUNS: Record<ReferenceKind, string> = {
  needEntities: "a Need entity",
  needNpdSubcategories: "a subcategory",
  externalPartnerOrganizations: "an organization",
  departments: "a department",
  partnerDris: "a DRI"
};

/**
 * The five roles, in the words the Console access screen uses for them.
 *
 * "Roles changed" is the line an access review turns on, and the stored value is a
 * set of wire names — `partner-writer` — while the screen above it offers a level per
 * population. Rendering the wire names would make the history and the control that
 * produced it two different vocabularies for the same fact, which is the thing a
 * reviewer has to reconcile by hand.
 *
 * `iam-admin` maps to itself, and the entry is kept rather than left to the fallback
 * for the reason FIELD_LABELS keeps `code` and `aliases`: this map is where a reader
 * learns which values a roles snapshot can contain, and an incomplete list reads as
 * though the missing one were impossible. It is also literally the checkbox's label
 * on the screen, which is the claim being made by mapping it at all. Its rendered
 * output is therefore an unusable guard for that one entry — the fallback produces
 * the same string — so the guard is that the entry is written down, asserted on the
 * map. Same shape as the FIELD_LABELS assertions in audit.test.ts.
 *
 * Exported for that assertion, and for the Go one:
 * `TestEveryRoleHasAConsoleLabel` holds this map to `auth.Roles`, so the sixth role —
 * which role.go says is "a schema migration plus a change here plus a change to the
 * matrix test" — cannot reach this screen as a raw wire name.
 */
export const ROLE_LABELS: Record<string, string> = {
  "iam-admin": "iam-admin",
  "partner-reader": "Partners (read)",
  "partner-writer": "Partners (write)",
  "odc-reader": "On-demand contractors (read)",
  "odc-writer": "On-demand contractors (write)"
};

/**
 * A recorded role set, as a person would read it.
 *
 * Two writers record this key and they disagree on its shape. `adminSnapshot` in
 * internal/people/admin.go joins the roles with commas into a string; migration 000008
 * records a JSON array. Both are permanent, because audit_log refuses UPDATE, so both
 * have to read.
 *
 * One path handles them, and the reason is worth stating rather than relying on: a
 * JavaScript array stringifies by joining its elements with a comma, which is exactly
 * the shape the other producer writes. So `String(value)` normalises the two before
 * anything is split. An `Array.isArray` branch stood here first and was removed as
 * unreachable behaviour — for every value either producer can write it computed the
 * same answer, which made it a branch no test could distinguish and therefore no test
 * could protect.
 *
 * That equivalence holds because a role is `[a-z-]+`, per the `role` table. A value
 * containing a comma would split into two, and would be a role name the model does
 * not permit.
 *
 * An unrecognised role is shown exactly as recorded. A role could be removed from the
 * model while snapshots naming it stay, and inventing a label for one would be a
 * confident lie about what somebody was granted.
 */
function rolesValue(value: unknown): string {
  const roles = String(value)
    .split(",")
    .map((role) => role.trim())
    .filter((role) => role !== "");
  if (roles.length === 0) {
    // Every other absence in this file reads as an em dash, and a transition into or
    // out of an empty set is exactly where that reads correctly: "— → Partners
    // (read)" is a grant, and the arrow carries the meaning that "no roles" would
    // have to spell out.
    return "—";
  }
  return roles.map((role) => ROLE_LABELS[role] ?? role).join(", ");
}

/** The console's Lookups query, narrowed to the two fields a name needs. */
export type AuditLookups = {
  [K in Exclude<ReferenceKind, "partnerDris">]: readonly { id: string; name: string }[];
};

/** id → name per lookup. */
export type AuditNames = Record<ReferenceKind, ReadonlyMap<string, string>>;

/**
 * Indexes the lookups the console already loads, or reports that it has none yet.
 *
 * `null` for absent lookups rather than three empty maps: "this id is not in a list
 * I have" and "I have no list" are different facts with different remedies, and
 * three empty maps would render every id as the first while meaning the second.
 */
export function buildAuditNames(
  lookups: AuditLookups | null | undefined,
  partnerDris: readonly { id: string; name: string }[] = []
): AuditNames | null {
  if (!lookups) {
    return null;
  }
  const index = (rows: readonly { id: string; name: string }[]) =>
    new Map(rows.map((r) => [r.id, r.name]));
  return {
    needEntities: index(lookups.needEntities),
    needNpdSubcategories: index(lookups.needNpdSubcategories),
    externalPartnerOrganizations: index(lookups.externalPartnerOrganizations),
    departments: index(lookups.departments),
    partnerDris: index(partnerDris)
  };
}

/**
 * Collapses the rows of one action into one event.
 *
 * A shared request id is necessary but **not sufficient** to prove two rows were one
 * action. It used to be doubly insufficient, because the HTTP middleware honoured a
 * caller-supplied `X-Request-Id`; INFRA-601 fixed that, so the id is now always
 * server-generated and no longer something a caller can choose. What remains is that the
 * id identifies a *request*, not an action: one GraphQL request may carry several root
 * mutation fields, which legitimately share an id while being separate actions. Relaxing
 * the rule below therefore still needs a grouping id scoped to a single root field, which
 * is not what `requestId` is.
 *
 * So only the pair that is genuinely one action gets merged: exactly one
 * `identity.create` with exactly one `engagement.create`, which is what `createIdentity`
 * writes. Anything else stands alone. Being over-eager here would file unrelated entries
 * under a single summary, which is the one thing an audit log must never do.
 */
export function groupAuditRows(rows: readonly AuditRow[]): AuditEvent[] {
  const mergeable = new Set<string>();
  const byRequest = new Map<string, AuditRow[]>();
  for (const row of rows) {
    if (!row.requestId) {
      continue;
    }
    const list = byRequest.get(row.requestId) ?? [];
    list.push(row);
    byRequest.set(row.requestId, list);
  }
  for (const group of byRequest.values()) {
    const identities = group.filter((r) => r.action === "identity.create");
    const engagements = group.filter((r) => r.action === "engagement.create");
    // Exactly one of each, and nothing else sharing the id. Two createIdentity calls in
    // one request would give two of each, and merging all four into one event is a lie.
    if (group.length === 2 && identities.length === 1 && engagements.length === 1) {
      mergeable.add(identities[0]!.id);
      mergeable.add(engagements[0]!.id);
    }
  }

  const events: AuditEvent[] = [];
  const pairEvents = new Map<string, AuditEvent>();

  for (const row of rows) {
    const pairKey = mergeable.has(row.id) ? row.requestId : undefined;
    const existing = pairKey ? pairEvents.get(pairKey) : undefined;
    if (existing) {
      existing.rows.push(row);
      // An identity row carries no population and an engagement row does; the event
      // should show the one that exists rather than whichever arrived first.
      existing.population = existing.population ?? row.population ?? null;
      continue;
    }

    const event: AuditEvent = {
      key: pairKey ?? row.id,
      occurredAt: row.occurredAt,
      actorEmail: row.actorEmail,
      population: row.population ?? null,
      rows: [row]
    };
    events.push(event);
    if (pairKey) {
      pairEvents.set(pairKey, event);
    }
  }

  return events;
}

const ACTION_TITLES: Record<string, string> = {
  "identity.create": "Person created",
  "identity.update": "Details changed",
  // The person's system access turning off — by Remove system access, whose contracts
  // carry on, or by ending or deleting their last term in Okta — and somebody giving
  // it: back after a removal, or for the first time to somebody created without it.
  "identity.deactivate_in_okta": "System access removed",
  "identity.remove_system_access": "System access removed",
  "identity.restore_system_access": "System access restored",
  "identity.grant_system_access": "System access granted",
  "engagement.create": "Engagement added",
  "engagement.terminate": "Ended",
  // The three lifecycle actions that existed before status became derived. Activate
  // and suspend can no longer be written and deactivate was renamed, but entries
  // naming them are permanent, so all three still need a title.
  "engagement.activate": "Activated",
  "engagement.suspend": "Suspended",
  "engagement.deactivate": "Ended",
  "engagement.revive": "Revived",
  // Removed, not deleted, and not "Ended": this is the one engagement event that says
  // the term should never have been recorded, and a reader scanning a history has to
  // be able to tell it from the termination two lines up, which says the opposite —
  // that it happened and then stopped. The word is the one the button and the dialog
  // use, so the entry names the action the operator took.
  "engagement.delete": "Engagement removed",
  "engagement.set_beam_detail": "Beam detail changed",
  // The dates get a title each rather than sharing one. They are separate mutations
  // recording separate facts — when the term runs from, and when it is contracted to
  // run out — and a reader scanning a history wants to know which of the two moved
  // without opening the row. That is the opposite call from "engagement.update"
  // below, which covers four fields, because those four move together in one write.
  "engagement.set_start_date": "Start date changed",
  "engagement.set_end_date": "Contracted end changed",
  // The third date, and the one that shares a column with "Ended" above. Its own
  // title because the two are different events to a reader — somebody left, versus
  // the day we recorded was wrong — and "Ended on" is the name this field already
  // carries in FIELD_LABELS, so the title is built from that rather than a fourth
  // word for the same date.
  "engagement.set_termination_date": "Ended-on date corrected",
  // One title for all four fields it can move — the entity, the subcategory, the
  // firm and the title. changedFields already names which of them did, and a title
  // per field would claim four kinds of event where the record has one.
  "engagement.update": "Engagement changed",
  "department.create": "Department added",
  "department.update": "Department changed",
  "department.archive": "Department archived",
  "department.unarchive": "Department restored",
  "admin_user.create": "Console access granted",
  "admin_user.set_roles": "Roles changed",
  "admin_user.set_status": "Access status changed",
  // The founding grants, written by migration 000008 and by nothing in Go. They are
  // the root of the chain of console access: there was no prior admin who could have
  // made them, so each is attributed to the grantee rather than to a grantor, and
  // "at setup" is the part of that a reader needs — it says why no one granted it
  // without claiming the record is incomplete. Untitled until INFRA-685, because
  // these are unscoped entries and the only history panel until then was per-person.
  "admin_user.bootstrap": "Console access granted at setup",
  // Written by internal/bootstrap/local_admins.go, which grants iam-admin from the
  // LOCAL_ADMINS_FILE under APP_ENV=local and attributes it to a local-seed actor.
  // Only ever in a developer's database, and titled anyway: it renders in the same
  // panel, and "granted from the local admins file" is what distinguishes it from a
  // grant somebody actually made. Its snapshot also carries a `source` key, which is
  // why FIELD_LABELS names one.
  "admin_user.grant_role": "Console access granted locally",
  "external_partner_organization.create": "Organization added",
  "external_partner_organization.update": "Organization changed",
  // Retired and restored rather than archived and unarchived: the reader of a
  // person's history is being told what happened to a firm, and "retired" is the
  // word for it outside the schema. Two actions rather than one carrying a boolean,
  // so the title can say the direction without reading the snapshot.
  "external_partner_organization.archive": "Organization retired",
  "external_partner_organization.unarchive": "Organization restored",
  // The one entry whose subject no longer exists when it is read. Its snapshot is the
  // only surviving record of the firm, which is what lets the delete be permitted at
  // all — and why "Organization deleted" is worth saying plainly rather than folding
  // into the retirement wording above: retiring is reversible and this is not.
  "external_partner_organization.delete": "Organization deleted",
  // Written when an update replaces a legal name. Shown as its own event rather than
  // folded into the "Details changed" row beside it: two entities were written, and
  // groupAuditRows merges only the create pair — being over-eager there would file
  // unrelated entries under one summary.
  "identity_former_name.create": "Former name recorded"
};

/**
 * Every create action fills changedFields with everything it set, so the "do not
 * enumerate a create's fields" rule keys off the shape of the action rather than a list
 * of names. With an allowlist, the next create added falls through to the update path
 * and silently starts dumping columns again.
 *
 * `admin_user.bootstrap` is the one create the shape rule cannot see, and it is named
 * here rather than renamed there: migration 000008 wrote it, audit_log refuses UPDATE,
 * and so those rows say "bootstrap" permanently. It creates an account and records an
 * after-snapshot only, which is what this predicate is being asked. Without the
 * exception each founding grant renders a transition out of nothing — "— →
 * jane@need.ai" — claiming a prior state that never existed, on the screen an access
 * review reads.
 *
 * This stays one named exception rather than a general "no before-snapshot means a
 * create". That rule would be true of every row today and would also quietly absorb a
 * future write path that forgot to record a before, showing a change of something as
 * a creation of it.
 */
export function isCreateAction(action: string): boolean {
  return action.endsWith(".create") || action === "admin_user.bootstrap";
}

export function titleFor(action: string): string {
  const known = ACTION_TITLES[action];
  if (known) {
    return known;
  }
  // Unmapped actions degrade to the raw string rather than to blank: a history that
  // silently omits an event is worse than one that shows an ugly name.
  return action;
}

/**
 * describeEvent produces the headline and the one-line detail.
 *
 * The rule that matters: **a create does not list its fields.** Every field was set,
 * so naming eleven of them says nothing except that a form was submitted. An update
 * is the opposite — the changed set is the entire content of the event.
 */
export function describeEvent(event: AuditEvent): { title: string; detail: string } {
  const actions = new Set(event.rows.map((r) => r.action));
  const population = event.population ? humanPopulation(event.population) : null;

  if (actions.has("identity.create")) {
    return {
      title: "Person created",
      detail: population ? `${population} engagement` : "Record created"
    };
  }

  // A second engagement on an existing person: a rehire, or someone picking up work
  // in the other population.
  if (actions.has("engagement.create")) {
    return {
      title: "Engagement added",
      detail: population ?? ""
    };
  }

  // A console-access change, whose detail is whose access it was.
  //
  // That is the one fact the shared renderer cannot get from anywhere else, and the
  // one an access review is built on: "Roles changed" without a subject is unusable,
  // and the actor column beside it names the person who made the change, not the
  // person it was made to. The subject is read out of the snapshot rather than
  // resolved from `entityId` against the current account list, because the snapshot
  // is what survives — an account that has since been renamed, or whose row is gone,
  // still reads as the address it was at the time, which is what the entry is about.
  //
  // It also displaces the default detail, which for these actions restated the title:
  // "Roles changed — Roles", "Access status changed — Status".
  //
  // Matched on the entity prefix rather than on the four action names, so a fifth
  // admin_user action gets a subject the day it is added instead of the day somebody
  // notices it has none. Same reason `unscopedAuditLog` filters by entity type.
  const adminAction = [...actions].find((a) => a.startsWith("admin_user."));
  if (adminAction) {
    return { title: titleFor(adminAction), detail: adminUserSubject(event) };
  }

  const changed = [...new Set(event.rows.flatMap((r) => r.changedFields))];
  const lifecycle = [...actions].find((a) =>
    [
      "engagement.terminate",
      // Historical, and permanent: see ACTION_TITLES.
      "engagement.activate",
      "engagement.suspend",
      "engagement.deactivate"
    ].includes(a)
  );
  if (lifecycle) {
    return {
      title: titleFor(lifecycle),
      detail: population ? `${population} engagement` : ""
    };
  }

  // Removing a term, whose changed set is the term's *entire* snapshot for the reason
  // the organization delete's is: the row went away and every column with it, so the
  // detail would be a column dump saying nothing except that a delete happened. The
  // population is the detail worth having, the same one the lifecycle branch above
  // gives, because a person may hold a term in each and the title alone would not say
  // which one went.
  if (actions.has("engagement.delete")) {
    return {
      title: titleFor("engagement.delete"),
      detail: population ? `${population} engagement` : ""
    };
  }

  // The organization actions whose own title is the whole event, so listing the
  // fields beside it restates the title as though it were extra information. Same
  // reasoning as the create rule below, arrived at from two different directions.
  //
  // Retiring and restoring write one field, archived_at, and the title already says
  // which direction it went.
  //
  // Deleting is the opposite extreme and lands in the same place: its changed set is
  // the organization's *entire* snapshot, because the row went away and every column
  // with it, so the detail would read "Archived on, Name" — a column dump that says
  // nothing except that a delete happened. This is the branch that keeps it out of the
  // default case, which is where it would otherwise fall.
  const wholeEvent = [...actions].find((a) =>
    [
      "department.archive",
      "department.unarchive",
      "external_partner_organization.archive",
      "external_partner_organization.unarchive",
      "external_partner_organization.delete"
    ].includes(a)
  );
  if (wholeEvent) {
    return { title: titleFor(wholeEvent), detail: "" };
  }

  // external_partner_organization.create reaches here. It sets every field too, so
  // listing them would be exactly the noise this rule removes.
  //
  // admin_user.create used to as well, and no longer does: the console-access branch
  // above intercepts every admin_user action to name its subject. The outcome is the
  // same either way — that branch supplies a subject where this one supplied nothing,
  // and neither dumps the columns — which is why nothing broke when it moved. Worth
  // saying, because the branch order is what makes it true.
  const create = [...actions].find(isCreateAction);
  if (create) {
    return { title: titleFor(create), detail: "" };
  }

  // groupAuditRows never produces an empty group, but the index signature does not
  // know that and a crash here would take out the whole history.
  const first = event.rows[0];
  return {
    title: first ? titleFor(first.action) : "Changed",
    detail: listFields(changed)
  };
}

/**
 * Whose console access an admin_user event was about, or "" if the entry does not say.
 *
 * The after-snapshot first: on a grant it is the only snapshot there is, and on a
 * change of roles or status the address is the same in both. The before-snapshot is
 * the fallback rather than the other way round for the case that has not arrived yet
 * — an entry whose after is absent, which is what a revocation that deleted the row
 * would write.
 *
 * "" rather than a placeholder when neither snapshot carries an email. The row then
 * reads as its title alone, which is true; inventing "unknown account" would put a
 * claim in front of a reviewer that the record does not make.
 */
function adminUserSubject(event: AuditEvent): string {
  for (const row of event.rows) {
    for (const snapshot of [parseSnapshot(row.after), parseSnapshot(row.before)]) {
      const email = snapshot.email;
      if (typeof email === "string" && email !== "") {
        return email;
      }
    }
  }
  return "";
}

/** Three names read as a sentence; eleven read as a column dump, so the tail is
 * counted rather than listed. The expander has the full set.
 *
 * De-duplicated on the **label**, not the column: entries written while migration
 * 000013's two location columns coexisted list both, and they are one field, so
 * without this a single work-location change reads as "Work country, Work country".
 * Callers already de-duplicate the columns, which does not help — the two keys differ.
 */
function listFields(columns: readonly string[]): string {
  const labels = [...new Set(columns.map(humanField))];
  if (labels.length === 0) {
    return "";
  }
  if (labels.length <= 3) {
    return labels.join(", ");
  }
  return `${labels.slice(0, 3).join(", ")} and ${labels.length - 3} more`;
}

export type FieldChange = {
  field: string;
  before: string;
  after: string;
  /**
   * Whether to show the before → after transition. False only on a create, where there
   * is no prior state to show — a create being whatever `isCreateAction` says, which
   * is the `.create` suffix plus the one founding grant a migration spelled otherwise.
   *
   * This cannot be inferred from `before` being empty. An update that fills a
   * previously blank field — a gender or middle name nobody had recorded — has an empty
   * before and a real transition, and treating it as a create would answer "what is it
   * now" while silently dropping "what was it before", which is the question the
   * expander exists to answer.
   */
  hadPriorState: boolean;
};

/**
 * Pairs the before and after snapshots for the columns a row actually changed.
 *
 * Values, not just column names, are what someone checking the record came for —
 * "Last name: Smith → Jones" answers the question that "last_name" only poses.
 *
 * One row per label, for the same reason listFields de-duplicates: an entry written
 * while migration 000013's two location columns coexisted names both, and one field
 * changed. The first of them wins, which for those entries is the pre-rename column —
 * the two describe the same write, and its snapshot carries the value the field held
 * before the backfill narrowed it to a code.
 */
export function fieldChanges(row: AuditRow, names: AuditNames | null): FieldChange[] {
  const before = parseSnapshot(row.before);
  const after = parseSnapshot(row.after);
  const hadPriorState = !isCreateAction(row.action);

  const seen = new Set<string>();
  const changes: FieldChange[] = [];
  for (const column of row.changedFields) {
    const field = humanField(column);
    if (seen.has(field)) {
      continue;
    }
    seen.add(field);
    changes.push({
      field,
      before: renderValue(column, before[column], names),
      after: renderValue(column, after[column], names),
      hadPriorState
    });
  }
  return changes;
}

function parseSnapshot(raw: string | null | undefined): Record<string, unknown> {
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
  } catch {
    // A snapshot that will not parse is a reason to show nothing for that row, not to
    // break the whole history.
    return {};
  }
}

/**
 * renderValue turns one recorded value into what the screen shows.
 *
 * Emptiness is decided first, because it is not a failure to resolve: an engagement
 * with no partner organization stores a null there, and "an organization that no
 * longer exists" is not what a null means.
 */
function renderValue(column: string, value: unknown, names: AuditNames | null): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  // The one column whose value is a set rather than a scalar. A single case rather
  // than a second map keyed by column: REFERENCE_FIELDS is a map because it has three
  // entries that each pick a different lookup, and one entry choosing nothing would
  // be a table pretending to be extensible.
  if (column === "roles") {
    return rolesValue(value);
  }
  const text = String(value);
  const kind = REFERENCE_FIELDS[column];
  return kind ? referenceValue(kind, text, names) : text;
}

/**
 * The name behind a stored id, or an honest account of why there is not one.
 *
 * Neither fallback is the bare id, which is the complaint this exists to answer, and
 * neither is a bare "Unknown", which would leave a reader nothing to carry to
 * somebody who can find out. The id's first group is short enough to read and long
 * enough to identify the row in the log, or in the entry that recorded its deletion.
 *
 * Neither fallback says the row was *deleted*, and saying so would now be wrong
 * almost every time. That was this case's original reason — organizations could be
 * deleted (INFRA-611) while audit_log refuses UPDATE and DELETE by trigger, so a
 * snapshot outlived the row it named — and it has since been false in both
 * directions: INFRA-656 removed delete outright, and INFRA-671 brought it back
 * *guarded on this exact hazard*. An organization is now refused deletion while any
 * audit snapshot holds its id, so a stored `external_partner_organization_id` is
 * meant to resolve for good.
 *
 * Which leaves the honest reasons this branch still runs, none of them a deletion.
 * `useLookups` holds its answer for ten minutes, so an organization somebody else
 * created since this page loaded is missing from the list. The lookups query can
 * fail. And an append-only trail is the wrong place to assume a reference resolves at
 * all: the guard is a runtime check in one write path, and entries predating it can
 * carry an id nothing can name.
 *
 * So the wording stays exactly as it is. It claims only what is known — this console
 * has no name for this id — and every scenario that reaches it is one where "the firm
 * was deleted" would be a confident lie.
 */
function referenceValue(kind: ReferenceKind, id: string, names: AuditNames | null): string {
  const noun = REFERENCE_NOUNS[kind];
  if (!names) {
    // Separate from the sentence below because the remedy is different: nothing
    // loaded, so reloading is the answer, and no claim about the row is being made
    // at all.
    return `${noun} whose name could not be loaded (${idFragment(id)})`;
  }
  const name = names[kind].get(id);
  if (name) {
    return name;
  }
  // The wording covers all three kinds even though only an organization can reach it
  // today — entities and subcategories have no delete path at all — because a
  // resolver that knows which of its own keys cannot fail is a resolver that renders
  // "undefined" on the day one more of them can.
  return `${noun} this console cannot name (${idFragment(id)})`;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The first group of a UUID, which is the part people quote to each other.
 *
 * Recognising the whole shape before cutting, rather than taking everything up to
 * the first dash: anything else stored in one of these columns is shown in full,
 * because a value shortened on a guess is a value that matches nothing when someone
 * goes looking for it. This is presentation of a value already known to be a
 * reference — which column to resolve at all is decided by name, above.
 */
function idFragment(id: string): string {
  return UUID.test(id) ? id.slice(0, 8) : id;
}
